import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [orderId, setOrderId] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const pollPaymentStatus = async () => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }

      try {
        const response = await axios.get(`${API}/payment-status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setOrderId(data.order_id);
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return;
        }

        // Continue polling if still pending
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } catch (error) {
        setAttempts(prev => prev + 1);
        if (attempts >= maxAttempts - 1) {
          setStatus('failed');
        } else {
          setTimeout(pollPaymentStatus, pollInterval);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts, clearCart]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0047AB] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-outfit font-semibold text-slate-900 mb-2">
            Verifying Payment...
          </h2>
          <p className="text-slate-500">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
            Payment Failed
          </h2>
          <p className="text-slate-500 mb-6">
            We couldn't verify your payment. Please try again or contact support if the issue persists.
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/checkout')}
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/support')}
              className="bg-[#0047AB] hover:bg-[#003380]"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
          Payment Successful!
        </h2>
        <p className="text-slate-500 mb-2">
          Thank you for your order. Your payment has been processed successfully.
        </p>
        {orderId && (
          <p className="text-sm text-slate-400 mb-6">
            Order ID: <span className="font-mono">{orderId.slice(0, 8)}...</span>
          </p>
        )}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <p className="text-sm text-slate-600">
            A confirmation email will be sent to your registered email address. 
            Our team will contact you regarding delivery details.
          </p>
        </div>
        <div className="flex gap-4 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate('/my-orders')}
          >
            View Orders
          </Button>
          <Button
            onClick={() => navigate('/products')}
            className="bg-[#0047AB] hover:bg-[#003380]"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
